import React, { useState, useEffect, useContext, useCallback } from 'react'; // Import useCallback
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import { Role } from '../../utils/enums'; // Import the Role enum
import { NotificationContext } from '../../context/NotificationContext';
import './UserManagement.css';
import { getUserDiscounts, createDiscount, updateDiscount, deleteDiscount } from '../../services/discountService';
import { BASE_URL } from '../../utils/config';
import { apiFetch } from '../../utils/apiClient';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '', // The login function from UserContext will handle all state updates.
    role: Role.User, // Default role for edit form (will be overwritten when fetching user)
    preferences: { subscribe: true },
    creditPoints: 0, // Added for edit form
    phoneCountryCode: '',
    phoneNumber: '',
    ageOver18: false,
  });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showUserDetails, setShowUserDetails] = useState(false);
  // New state for discounts
  const [userDiscounts, setUserDiscounts] = useState([]);
  const [discountsLoading, setDiscountsLoading] = useState(false);
  const [discountsError, setDiscountsError] = useState(null);

  const { user, login } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);
  const navigate = useNavigate();

  // Fetch all users - wrapped in useCallback
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BASE_URL}/authUser/users`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch users (${response.status})`);
      }

      const data = await response.json();
      //      console.log('Fetched users list:', data); // Debug log
      setUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.token]);

  // Fetch single user
  const fetchUser = async (userId) => {
    setError(null);
    setUserDiscounts([]); // Clear previous user's discounts immediately
    try {
      const response = await fetch(`${BASE_URL}/authUser/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch user (${response.status})`);
      }

      const data = await response.json();
      //      console.log('Fetched user data:', data); // Debug log
      setSelectedUser(data);
      setEditForm({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        role: data.role ?? Role.User, // Use role from data, default to User if null/undefined
        preferences: {
          subscribe: data.preferences?.subscribe ?? true,
        },
        creditPoints: data.creditPoints ?? 0, // Add creditPoints
        phoneCountryCode: data.phoneCountryCode || '',
        phoneNumber: data.phoneNumber || '',
        ageOver18: data.ageOver18 ?? false,
      });
      setIsEditing(true);
      setShowUserDetails(false);
      fetchUserDiscounts(userId); // Fetch the correct discounts for the user being edited
    } catch (err) {
      console.error('Error fetching user:', err);
      setError(err.message);
      setSelectedUser(null);
      setIsEditing(false);
    }
  };

  const fetchUserDiscounts = async (userId) => {
    setDiscountsLoading(true);
    setDiscountsError(null);
    try {
      const discounts = await getUserDiscounts(userId, user.token);
      setUserDiscounts(discounts);
    } catch (err) {
      setDiscountsError('Failed to load discounts.');
      addNotification(err.message, 'error');
    } finally {
      setDiscountsLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Basic validation in case of invalid date string
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    // Using en-CA format (YYYY-MM-DD) which is often less ambiguous, or en-GB (DD/MM/YYYY)
    return date.toLocaleDateString('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  // Format date and time for display
  const formatDateTime = (dateString) => {
    if (!dateString) {
      return 'Never';
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  // Handle row click
  const handleRowClick = async (userId) => {
    if (isEditing) return; // Don't show details while editing
    setError(null);
    try {
      // Use BASE_URL consistently
      const response = await fetch(`${BASE_URL}/authUser/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch user details (${response.status})`);
      }

      const data = await response.json();
      setSelectedUser(data);
      setShowUserDetails(true);
      fetchUserDiscounts(userId); // Fetch discounts when user is selected
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError(err.message);
      setSelectedUser(null); // Clear selected user on error
      setShowUserDetails(false); // Hide details view on error
    }
  };

  const handleCreateDiscount = async (discountData) => {
    if (!selectedUser) return;
    try {
      await createDiscount(selectedUser._id, discountData, user.token);
      addNotification('Discount created successfully!', 'success');
      fetchUserDiscounts(selectedUser._id); // Refresh list
    } catch (err) {
      addNotification(`Error creating discount: ${err.message}`, 'error');
    }
  };

  const handleUpdateDiscount = async (discountId, discountData) => {
    if (!selectedUser) return;
    try {
      await updateDiscount(discountId, discountData, user.token);
      addNotification('Discount updated successfully!', 'success');
      fetchUserDiscounts(selectedUser._id); // Refresh list
    } catch (err) {
      addNotification(`Error updating discount: ${err.message}`, 'error');
    }
  };

  const handleDeleteDiscount = async (discountId) => {
    if (!selectedUser) return;

    addNotification(
      'Are you sure you want to delete this discount?',
      'warning',
      0,
      true,
      [
        { text: 'Cancel', onClick: () => {} },
        {
          text: 'Yes, Delete',
          onClick: async () => {
            try {
              await deleteDiscount(discountId, user.token);
              addNotification('Discount deleted successfully!', 'success');
              fetchUserDiscounts(selectedUser._id); // Refresh list
            } catch (err) {
              addNotification(`Error deleting discount: ${err.message}`, 'error');
            }
          },
        },
      ],
      'delete-discount-confirmation'
    );
  };

  // Update user
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setError(null);
    if (!selectedUser?._id) {
      setError('No user selected for update.');
      return;
    }

    const updateData = {
      ...editForm,
      // creditPoints is already in editForm
    };

    console.log('Sending update data:', updateData); // Debug log

    try {
      const response = await fetch(`${BASE_URL}/authUser/users/${selectedUser._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update user (${response.status})`);
      }

      const updatedUser = await response.json();
      console.log('Server response:', updatedUser); // Debug log

      // Refresh users list
      fetchUsers();
      setIsEditing(false);
      setSelectedUser(null);
      addNotification('User updated successfully!', 'success');
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.message);
    }
  };

  // Delete user
  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    setError(null);
    try {
      // Use BASE_URL consistently
      const response = await fetch(`${BASE_URL}/authUser/users/${userId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete user (${response.status})`);
      }

      // Refresh users list
      fetchUsers();
      // If the deleted user was selected, clear the selection/details/edit form
      if (selectedUser?._id === userId) {
        setSelectedUser(null);
        setIsEditing(false);
        setShowUserDetails(false);
      }
      addNotification('User deleted successfully!', 'success');
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.message);
    }
  };

  const handleLoginAs = async (targetUserId, targetUsername) => {
    // if (!window.confirm(`Are you sure you want to log in as ${targetUsername}? Your current admin session will be paused.`)) {
    //   return;
    // }
    setError(null);
    try {
      // Before impersonating, save the current admin's refresh token
      const adminRefreshToken = localStorage.getItem('refreshToken');
      if (adminRefreshToken) {
        localStorage.setItem('adminRefreshToken', adminRefreshToken);
      } else {
        addNotification('Could not find the original admin session token. Impersonation aborted.', 'error');
        console.error('Impersonation error: admin refresh token not found in localStorage.');
        return;
      }

      const response = await apiFetch(`${BASE_URL}/authUser/impersonate/${targetUserId}`, { method: 'POST' }, user.token);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // If impersonation fails, clear the stored admin token to prevent being stuck.
        localStorage.removeItem('adminRefreshToken');
        throw new Error(errorData.message || 'Failed to impersonate user.');
      }

      const data = await response.json();

      // Use the centralized login function, passing the original admin's ID as the impersonatorId
      login(data, user.id);

      addNotification(`Successfully logged in as ${data.user.username}.`, 'success');
      navigate('/'); // Navigate to homepage as the new user
    } catch (err) {
      console.error('Impersonation error:', err);
      setError(err.message);
      // Also clear the token on any other catch
      localStorage.removeItem('adminRefreshToken');
    }
  };

  // Initial fetch of users when the component mounts or fetchUsers changes (due to token change)
  useEffect(() => {
    // Only fetch if the user token is available (user is likely logged in)
    if (user?.token) {
      fetchUsers();
    } else {
      // Handle case where user is not logged in or token is missing
      setIsLoading(false);
      setError('Authentication required to manage users.');
      setUsers([]); // Ensure users list is empty
    }
    // fetchUsers is now stable due to useCallback, so this effect runs
    // when fetchUsers changes (i.e., when user.token changes) or on mount.
  }, [fetchUsers, user?.token]);

  if (isLoading) {
    return <div className="loading">Loading users...</div>;
  }

  // Display general error prominently if it exists
  // {error && <div className="error-message general-error">{error}</div>}

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h1>User Management</h1>
        <div className="header-actions">{/* Add button was here */}</div>
      </div>

      {/* Display general error prominently if it exists */}
      {error && <div className="error-message general-error">{error}</div>}

      <div className="content-wrapper">
        <div className="table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>#</th>
                {/*<th>ID</th>*/} {/* Usually not shown to regular admins */}
                <th>First Name</th>
                <th>Last Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th> {/* Changed header */}
                <th>Credits</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && !isLoading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center' }}>
                    No users found or not authorized.
                  </td>
                </tr>
              ) : (
                users.map(
                  (
                    u,
                    index // Renamed user to u to avoid conflict with context user
                  ) => (
                    <tr
                      key={u._id}
                      onClick={() => handleRowClick(u._id)}
                      className={selectedUser?._id === u._id ? 'selected' : ''}
                      style={{ cursor: 'pointer' }} // Indicate rows are clickable
                    >
                      <td>{index + 1}</td>
                      {/*<td>{u._id}</td>*/}
                      <td>{u.firstName}</td>
                      <td>{u.lastName}</td>
                      <td>{u.username}</td>
                      <td>{u.email}</td>
                      <td>{Object.keys(Role).find((key) => Role[key] === u.role) || 'Unknown'}</td> {/* Display role name */}
                      <td>{u.creditPoints ?? 0}</td>
                      <td>{formatDateTime(u.lastLoginAt)}</td>
                      <td className="actions-cell">
                        <button
                          className="edit-btn"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row click handler
                            fetchUser(u._id);
                          }}
                          disabled={isEditing} // Disable if another form is open
                        >
                          Edit
                        </button>
                        <button
                          className="delete-btn"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row click handler
                            deleteUser(u._id);
                          }}
                          // Optionally disable deleting the logged-in user: disabled={u._id === user?._id}
                          disabled={isEditing} // Disable if another form is open
                        >
                          Delete
                        </button>
                        <button
                          className="login-as-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLoginAs(u._id, u.username);
                          }}
                          disabled={isEditing || u._id === user.id}
                        >
                          Login As
                        </button>
                      </td>
                    </tr>
                  )
                )
              )}
            </tbody>
          </table>
        </div>

        {/* Sidebar for user details, edit form, or new user form */}
        {(showUserDetails || isEditing) && (
          <div className="user-details-sidebar">
            {isEditing ? (
              <form onSubmit={handleUpdateUser} className="edit-form" noValidate>
                {/* Edit User Form Fields */}
                <div className="form-group">
                  <label htmlFor="edit-firstName">First Name*:</label>
                  <input type="text" id="edit-firstName" value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} required />
                  {/* Add validation display if needed */}
                </div>
                <div className="form-group">
                  <label htmlFor="edit-lastName">Last Name*:</label>
                  <input type="text" id="edit-lastName" value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} required />
                  {/* Add validation display if needed */}
                </div>
                <div className="form-group">
                  <label htmlFor="edit-phone">Phone Number:</label>
                  <input type="text" id="edit-phone" value={`${editForm.phoneCountryCode} ${editForm.phoneNumber}`.trim()} readOnly disabled />
                </div>
                {/* Credit Points Input */}
                <div className="form-group">
                  <label htmlFor="edit-creditPoints">Credit Points:</label>
                  <input type="number" id="edit-creditPoints" name="creditPoints" value={editForm.creditPoints} onChange={(e) => setEditForm({ ...editForm, creditPoints: parseInt(e.target.value, 10) || 0 })} />
                </div>
                <div className="form-group">
                  <label>Age Verified (18+):</label>
                  <div className="checkbox-container">
                    <input
                      type="checkbox"
                      id="edit-ageOver18"
                      name="ageOver18"
                      checked={editForm.ageOver18}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          ageOver18: e.target.checked,
                        })
                      }
                    />
                  </div>
                </div>
                {/* End of Credit Points Input */}
                {/* Role Select Input */}
                <div className="form-group">
                  <label htmlFor="edit-role">Role:</label>
                  <select id="edit-role" name="role" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: parseInt(e.target.value, 10) })}>
                    {Object.entries(Role).map(([key, value]) => (
                      // Only allow selecting roles up to the logged-in user's role level
                      // Admins can set any role, Sellers can set Seller or User/Guest, Users can only see their own role (not editable here)
                      // Assuming this form is only accessible to Admins for now based on AdminRoute
                      <option key={key} value={value}>
                        {key}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Newsletter Subscription:</label>
                  <div className="checkbox-container">
                    <input
                      type="checkbox"
                      id="edit-subscribe"
                      checked={editForm.preferences.subscribe}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          preferences: {
                            ...editForm.preferences,
                            subscribe: e.target.checked,
                          },
                        })
                      }
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="subscribe-btn">
                    Save Changes
                  </button>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => {
                      setIsEditing(false);
                      setSelectedUser(null);
                      setError(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              /* Show User Details View */
              selectedUser && ( // Ensure selectedUser is not null before rendering details
                <>
                  <div className="user-details-header">
                    <h2>User Details</h2>
                    <button
                      className="close-btn"
                      title="Close Panel"
                      onClick={() => {
                        setShowUserDetails(false);
                        setSelectedUser(null);
                        setError(null);
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <div className="user-details-content">
                    <div className="detail-group">
                      <label>ID:</label>
                      <span>{selectedUser._id}</span>
                    </div>
                    <div className="detail-group">
                      <label>First Name:</label>
                      <span>{selectedUser.firstName}</span>
                    </div>
                    <div className="detail-group">
                      <label>Last Name:</label>
                      <span>{selectedUser.lastName}</span>
                    </div>
                    <div className="detail-group">
                      <label>Username:</label>
                      <span>{selectedUser.username}</span>
                    </div>
                    <div className="detail-group">
                      <label>Email:</label>
                      <span>{selectedUser.email}</span>
                    </div>
                    <div className="detail-group">
                      <label>Age Verified (18+):</label>
                      <span>{selectedUser.ageOver18 ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="detail-group">
                      <label>Credit Points:</label>
                      <span>{selectedUser.creditPoints ?? 0}</span>
                    </div>
                    <div className="detail-group">
                      <label>Registration Date:</label>
                      <span>{formatDate(selectedUser.subscriptionTime)}</span>
                    </div>
                    <div className="detail-group">
                      <label>Last Login:</label>
                      <span>{formatDateTime(selectedUser.lastLoginAt)}</span>
                    </div>
                    <div className="detail-group">
                      <label>Preferences:</label>
                      <div className="preferences-details">
                        <div>
                          Items per page: {selectedUser.preferences?.itemsPerPage ?? 12} {/* Use nullish coalescing */}
                        </div>
                        <div>Subscribed to promotions: {selectedUser.preferences?.subscribe ? 'Yes' : 'No'}</div>
                      </div>
                    </div>
                    {/* Add Edit button here if desired */}
                    <div className="form-actions details-actions">
                      <button className="edit-btn" onClick={() => fetchUser(selectedUser._id)}>
                        Edit User
                      </button>
                    </div>
                  </div>
                </>
              )
            )}
          </div>
        )}
        {/* Discount Management Section - MOVED HERE */}
        {(showUserDetails || isEditing) && selectedUser && (
          <UserDiscounts
            discounts={userDiscounts}
            isLoading={discountsLoading}
            error={discountsError}
            onCreate={handleCreateDiscount}
            onUpdate={handleUpdateDiscount}
            onDelete={handleDeleteDiscount}
            existingProductTypes={userDiscounts.map((d) => d.productType)}
          />
        )}
      </div>
    </div>
  );
};

const UserDiscounts = ({ discounts, isLoading, error, onCreate, onUpdate, onDelete, existingProductTypes }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newDiscount, setNewDiscount] = useState({
    productType: 'report',
    discountPercentage: '',
    expiresAt: '',
  });
  const [editingDiscount, setEditingDiscount] = useState(null);

  const productTypeOptions = [
    { value: 'report', label: 'AI Report' },
    { value: 'promotion', label: 'Property Promotion' },
    { value: 'inquiry', label: 'Lead Unlock' },
  ];

  const handleAddClick = () => {
    setIsAdding(true);
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
    setNewDiscount({ productType: 'report', discountPercentage: '', expiresAt: '' });
  };

  const handleSaveNewDiscount = () => {
    if (!newDiscount.productType || newDiscount.discountPercentage === '') {
      // Basic validation
      return;
    }
    onCreate({
      ...newDiscount,
      discountPercentage: Number(newDiscount.discountPercentage),
    });
    handleCancelAdd();
  };

  const handleEdit = (discount) => {
    // Format expiresAt for the datetime-local input
    const expiresAtForInput = discount.expiresAt ? new Date(discount.expiresAt).toISOString().slice(0, 16) : '';
    setEditingDiscount({ ...discount, expiresAt: expiresAtForInput });
  };

  const handleSaveEdit = () => {
    if (!editingDiscount) return;
    const { _id, discountPercentage, isActive, expiresAt } = editingDiscount;
    onUpdate(_id, { discountPercentage: Number(discountPercentage), isActive, expiresAt });
    setEditingDiscount(null);
  };

  const formatDateTimeForDisplay = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  return (
    <div className="user-discounts-section">
      <div className="discounts-header">
        <h4>User Discounts</h4>
        {!isAdding && <button onClick={handleAddClick}>+ Add</button>}
      </div>

      {isLoading && <p className="loading-text">Loading discounts...</p>}
      {error && <p className="error-text">{error}</p>}

      <div className="discounts-list">
        {isAdding && (
          <div className="discount-edit-row">
            <select value={newDiscount.productType} onChange={(e) => setNewDiscount({ ...newDiscount, productType: e.target.value })}>
              {productTypeOptions
                .filter((opt) => !existingProductTypes.includes(opt.value))
                .map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
            </select>
            <input type="number" min="0" max="100" placeholder="%" value={newDiscount.discountPercentage} onChange={(e) => setNewDiscount({ ...newDiscount, discountPercentage: e.target.value })} />
            <input type="datetime-local" value={newDiscount.expiresAt} onChange={(e) => setNewDiscount({ ...newDiscount, expiresAt: e.target.value })} title="Expiration Date (optional)" />
            <div className="discount-actions">
              <button onClick={handleSaveNewDiscount} className="save-btn">
                Save
              </button>
              <button onClick={handleCancelAdd} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        )}

        {discounts.map((discount) => (
          <div key={discount._id} className="discount-row">
            {editingDiscount?._id === discount._id ? (
              <div className="discount-edit-row">
                <span>{productTypeOptions.find((p) => p.value === discount.productType)?.label || discount.productType}</span>
                <input type="number" min="0" max="100" value={editingDiscount.discountPercentage} onChange={(e) => setEditingDiscount({ ...editingDiscount, discountPercentage: e.target.value })} />
                <input type="datetime-local" value={editingDiscount.expiresAt} onChange={(e) => setEditingDiscount({ ...editingDiscount, expiresAt: e.target.value })} title="Expiration Date" />
                <input type="checkbox" checked={editingDiscount.isActive} onChange={(e) => setEditingDiscount({ ...editingDiscount, isActive: e.target.checked })} title="Is Active" />
                <div className="discount-actions">
                  <button onClick={handleSaveEdit} className="save-btn">
                    Save
                  </button>
                  <button onClick={() => setEditingDiscount(null)} className="cancel-btn">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="discount-info">
                  <span className="product-type">{productTypeOptions.find((p) => p.value === discount.productType)?.label || discount.productType}</span>
                  <span className="percentage">{discount.discountPercentage}%</span>
                  <span className={`status ${discount.isActive ? 'active' : 'inactive'}`}>{discount.isActive ? 'Active' : 'Inactive'}</span>
                  <span className="expires-at">Expires: {formatDateTimeForDisplay(discount.expiresAt)}</span>
                </div>
                <div className="discount-actions">
                  <button onClick={() => handleEdit(discount)} className="edit-btn">
                    Edit
                  </button>
                  <button onClick={() => onDelete(discount._id)} className="delete-btn">
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
        {!isLoading && discounts.length === 0 && !isAdding && <p className="no-discounts-text">No discounts for this user.</p>}
      </div>
    </div>
  );
};

export default UserManagement;
